import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, ChevronLeft, ChevronRight as ChevR } from "lucide-react";

const EXAMPLES = [
  {
    role: "Senior Product Manager",
    before: {
      score: 42,
      ats: 38,
      lines: [
        "Responsible for product roadmap and team alignment.",
        "Worked with engineering and design to ship features.",
        "Helped grow the product across multiple markets.",
        "Team player passionate about user experience.",
      ],
    },
    after: {
      score: 89,
      ats: 91,
      lines: [
        "Owned $42M ARR product line; defined and shipped 14-quarter roadmap with 12 engineers.",
        "Architected cross-functional discovery rituals that cut feature cycle time by 31%.",
        "Launched in 7 new markets; drove MAU from 180k → 1.1M in 14 months (+511%).",
        "Coached 3 PMs to senior+ promotion; established team OKR cadence still in use today.",
      ],
    },
    fix: "Quantified scope, replaced vague verbs, added concrete impact.",
  },
  {
    role: "Software Engineer",
    before: {
      score: 51,
      ats: 47,
      lines: [
        "Worked on backend services and APIs.",
        "Helped migrate the legacy database.",
        "Wrote unit tests and fixed bugs in production.",
        "Duties included code review and mentoring.",
      ],
    },
    after: {
      score: 91,
      ats: 93,
      lines: [
        "Designed and shipped 9 microservices (Go, gRPC) handling 1.2B requests/day at p99 < 80ms.",
        "Led Postgres → Aurora migration with zero downtime; reduced query cost by 62%.",
        "Raised test coverage from 41% → 88% across 3 services; cut prod incidents 70% YoY.",
        "Mentored 4 engineers; 2 promoted to mid-level within 9 months.",
      ],
    },
    fix: "Action verbs + numbers + tech specificity.",
  },
  {
    role: "Marketing Lead",
    before: {
      score: 38,
      ats: 44,
      lines: [
        "Ran marketing campaigns for the company brand.",
        "Worked on email marketing and social media content.",
        "Results-oriented marketer with a passion for storytelling.",
        "Helped with launching new products to the market.",
      ],
    },
    after: {
      score: 87,
      ats: 88,
      lines: [
        "Built and ran 31 multi-channel campaigns; generated $6.2M pipeline at 4.8× ROAS.",
        "Architected lifecycle email program (50+ flows) — lifted activation by 38% and ARR retention 11pts.",
        "Launched 4 product lines on schedule; secured TechCrunch + Product Hunt #1 in 2 of them.",
        "Scaled team 2 → 9 across content, growth, and brand; managed $1.8M annual budget.",
      ],
    },
    fix: "Replaced fluff with measurable outcomes and scope.",
  },
];

function Score({ value, size = "lg" }) {
  const color = value >= 80 ? "#007A5E" : value >= 60 ? "#DC1C22" : value >= 40 ? "#FFBF00" : "#FF2A00";
  const cls = size === "lg" ? "text-5xl" : "text-2xl";
  return (
    <span className={`font-display font-extrabold ${cls} tabular-nums`} style={{ color }}>
      {value}
    </span>
  );
}

export default function BeforeAfterGallery() {
  const [idx, setIdx] = useState(0);
  const ex = EXAMPLES[idx];
  const delta = ex.after.score - ex.before.score;

  return (
    <section className="border-b border-zinc-200" data-testid="before-after-gallery">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-4">◇ PROOF</div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Real resumes.<br />Real before-and-afters.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <div className="text-zinc-600 text-lg max-w-md lg:ml-auto">
              Anonymized examples from the last 90 days. Average lift: <span className="font-bold text-zinc-900">+39 points</span>.
            </div>
          </div>
        </div>

        {/* Role selector */}
        <div className="mb-8 flex flex-wrap gap-px bg-zinc-200 border border-zinc-200 w-fit" data-testid="gallery-tabs">
          {EXAMPLES.map((e, i) => (
            <button
              key={e.role}
              onClick={() => setIdx(i)}
              data-testid={`gallery-tab-${i}`}
              className={`px-5 py-2.5 text-sm font-medium transition-colors ${
                i === idx ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50 text-zinc-700"
              }`}
            >
              {e.role}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 relative">
          {/* BEFORE */}
          <div className="bg-white p-6 lg:p-10" data-testid="gallery-before">
            <div className="flex items-baseline justify-between mb-6">
              <div className="overline text-[#FF2A00]">BEFORE</div>
              <div className="text-right">
                <Score value={ex.before.score} />
                <div className="text-xs text-zinc-500 font-mono mt-1">ATS · {ex.before.ats}/100</div>
              </div>
            </div>
            <ul className="space-y-3">
              {ex.before.lines.map((line, i) => (
                <li key={i} className="font-mono text-[13px] text-zinc-500 leading-relaxed">
                  <span className="text-zinc-300 mr-2">→</span>
                  <span className="line-through decoration-zinc-300/60">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* AFTER */}
          <div className="bg-zinc-900 text-white p-6 lg:p-10 relative" data-testid="gallery-after">
            <div className="absolute -left-3 top-8 hidden md:flex w-12 h-12 bg-[#DC1C22] text-white items-center justify-center font-display font-extrabold">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div className="flex items-baseline justify-between mb-6">
              <div className="overline text-[#7CFFA8]">AFTER · ResumeLab Pro</div>
              <div className="text-right">
                <Score value={ex.after.score} />
                <div className="text-xs text-zinc-400 font-mono mt-1">ATS · {ex.after.ats}/100</div>
              </div>
            </div>
            <ul className="space-y-3">
              {ex.after.lines.map((line, i) => (
                <li key={i} className="font-mono text-[13px] text-zinc-100 leading-relaxed">
                  <span className="text-[#7CFFA8] mr-2">●</span>
                  {line}
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-zinc-700 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">FIX</div>
                <div className="text-sm text-zinc-200 max-w-md">{ex.fix}</div>
              </div>
              <div className="text-right shrink-0 ml-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">LIFT</div>
                <div className="font-display font-extrabold text-3xl text-[#7CFFA8]">+{delta}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-8">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIdx((idx - 1 + EXAMPLES.length) % EXAMPLES.length)}
              data-testid="gallery-prev"
              className="w-9 h-9 border border-zinc-200 hover:bg-zinc-900 hover:text-white inline-flex items-center justify-center"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIdx((idx + 1) % EXAMPLES.length)}
              data-testid="gallery-next"
              className="w-9 h-9 border border-zinc-200 hover:bg-zinc-900 hover:text-white inline-flex items-center justify-center"
              aria-label="Next"
            >
              <ChevR className="w-4 h-4" />
            </button>
            <span className="ml-3 text-xs text-zinc-500 font-mono">
              {String(idx + 1).padStart(2, "0")} / {String(EXAMPLES.length).padStart(2, "0")}
            </span>
          </div>
          <Link to="/evaluate" data-testid="gallery-cta" className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white">
            Get my after <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
